import { useEffect, useState } from 'react';
import { Link } from 'components/Elements';
import { Head } from 'components/Head';
import PropTypes from 'prop-types';
import logo from 'assets/logo2.png';
import Cookies from 'js-cookie';
import { Moon, Sun } from 'lucide-react';
import { Check } from 'react-feather';
import { useTheme } from 'hooks/useTheme'; // Global theming

export const Layout = ({ children, title }) => {
  const [showCookieNotice, setShowCookieNotice] = useState(false);
  const [localMode, setLocalMode] = useState(
    () => localStorage.getItem('themeMode') || 'light'
  );

  const { theme, background, darkMode, toggleDarkMode } = useTheme();

  useEffect(() => {
    const hasAccepted = Cookies.get('cookies_accepted');
    if (!hasAccepted) setShowCookieNotice(true);
  }, []);

  // Keep dark mode synced with ThemeProvider state
  useEffect(() => {
    if (darkMode && localMode !== 'dark') {
      setLocalMode('dark');
    } else if (!darkMode && localMode !== 'light') {
      setLocalMode('light');
    }
  }, [darkMode, localMode]);

  const handleToggleTheme = () => {
    toggleDarkMode();
    setLocalMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const loginPassword = Cookies.get('authentication_level_password');
  const loginGoogleAuth = Cookies.get('authentication_level_google_auth');
  const loginEmail = Cookies.get('authentication_level_email');
  const loginSMS = Cookies.get('authentication_level_sms');

  const securityLayers = [
    { label: 'Password', key: loginPassword },
    { label: 'Google authenticator', key: loginGoogleAuth },
    { label: 'Email Verification', key: loginEmail },
    { label: 'SMS Verification', key: loginSMS },
  ];

  const handleAcceptCookies = () => {
    Cookies.set('cookies_accepted', 'true', { expires: 365 });
    setShowCookieNotice(false);
  };

  return (
    <>
      <Head title={title} />
      <div
        className={`min-h-screen flex flex-col ${theme} relative transition-all duration-500`}
      >
        {/* Global Background Image */}
        <div className="absolute inset-0 pointer-events-none opacity-10 z-0">
          {background && (
            <img
              src={background}
              alt="Decorative Background"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        {/* Theme toggle */}
        <div className="absolute top-4 right-6 z-10">
          <button
            onClick={handleToggleTheme}
            className="p-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 dark:border-white/10 text-white"
            title="Toggle Theme"
          >
            {localMode === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center mt-4 mb-2 px-4 relative z-10">
          <Link
            className="flex flex-col items-center text-white hover:text-white"
            to="/"
          >
            <img className="h-14 w-auto mb-1" src={logo} alt="Wire24" />
            <span className="text-xs">Administrator ©</span>
          </Link>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex justify-center items-center px-0 relative z-10">
          <div className="w-full max-w-md bg-white/10 dark:bg-black/20 backdrop-blur-md border border-white/20 dark:border-white/10 shadow-2xl px-4 py-8 sm:px-8 rounded-3xl animate-slide-in-left">
            <div className="mb-4 text-center">
              <h2 className="text-xl font-bold text-white">{title}</h2>
            </div>
            {children}
          </div>

          {/* Security Layers */}
          <div className="absolute opacity-20 left-6 bottom-20 px-4 py-4 text-white text-sm w-56">
            <h4 className="text-white/80 mb-2 font-semibold text-start">
              Wire24 Security
            </h4>
            <hr className="border border-white/30 mb-3" />
            <ul className="space-y-2">
              {securityLayers.map((layer, index) =>
                layer.key ? (
                  <li key={index} className="flex justify-between items-start">
                    <span>{layer.label}</span>
                    <Check size={16} className="text-green-400" />
                  </li>
                ) : null
              )}
              {!securityLayers.some((layer) => layer.key) && (
                <li className="text-white/40 text-xs text-start">
                  No active session.
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-6 max-w-md mx-auto text-center text-white/40 text-xs px-4 pb-6 relative z-10">
          <p className="mb-2">
            By continuing, you agree to the{' '}
            <Link
              to="/administrator-agreement"
              className="hover:underline text-white/80"
            >
              Wire24 Administrator Agreement
            </Link>{' '}
            or other agreement for Wire24 services, and the{' '}
            <Link to="/privacy" className="hover:underline text-white/70 mx-1">
              Privacy Policy
            </Link>
            . This site uses essential cookies. See our{' '}
            <Link to="/cookies" className="hover:underline text-white/70">
              Cookie Notice
            </Link>{' '}
            for more information.
            <a
              href="https://wire24.io"
              target="_blank"
              rel="noreferrer"
              className="hover:underline text-white/70 ml-1"
            >
              Website
            </a>
          </p>
          <p>
            Property of Wire24 or its affiliates. All rights reserved ©{' '}
            {new Date().getFullYear()}
          </p>
        </footer>

        {/* Cookie Notice */}
        {showCookieNotice && (
          <div className="fixed bottom-4 right-4 bg-white/10 dark:bg-black/20 border border-white/20 dark:border-white/10 backdrop-blur-md shadow-lg rounded-xl p-4 max-w-xs text-sm text-white z-50">
            <p className="mb-2">
              We use cookies to enhance your experience. By continuing to visit
              this site you agree to our use of cookies.
            </p>
            <div className="flex justify-end">
              <button
                onClick={handleAcceptCookies}
                className="text-cyan-400 hover:underline"
              >
                Accept
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Animation */}
      <style>
        {`
          .animate-slide-in-left {
            animation: slideInFromLeft 0.6s ease-out forwards;
            transform: translateX(-100%);
            opacity: 0;
          }

          @keyframes slideInFromLeft {
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
};
